import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getCustomerServiceTenants } from '../../common/customerServiceTenants';
import { tenantSelected } from '../../action/authenticationEvents';

export type SortOrder = 'asc' | 'desc' | '';
export interface SortState {
    key: string;
    order: SortOrder;
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [100, 200, 300, 500];

interface UseMasterListStateArgs {
    /** Tenants the current user can see, in stable order. */
    accessibleTenants: string[];
    /** localStorage key for the per-page rows-per-page preference. */
    rowsPerPageStorageKey: string;
    /** Sort applied when no `sort` query param is present. */
    defaultSort: SortState;
    rowsPerPageOptions?: number[];
}

/**
 * Shared state machine for the all-tenant (Master Mode) list pages.
 *
 * Search query, sort, page and tenant selection are kept in the URL query
 * string (not React state) so that drilling into a detail page and coming
 * back — via the browser/topbar back button — restores the exact view the
 * user left, including their tenant filter. Search/sort/page mutations
 * use `replace` so they don't pollute the history stack. Tenant-filter
 * mutations and the drill-in navigation push entries, so the back button
 * steps backwards through the filter states until it reaches the
 * unfiltered starting view instead of leaving the all-tenant view.
 *
 * Tenant selection encoding:
 *   - param absent      → all accessible tenants selected (default)
 *   - `tenants=`        → none selected
 *   - `tenants=a,b`     → exactly the listed (still-accessible) tenants
 *
 * Sort encoding:
 *   - param absent      → `defaultSort`
 *   - `sort=`           → explicitly unsorted
 *   - `sort=key:order`  → that column/direction
 */
export function useMasterListState({
    accessibleTenants,
    rowsPerPageStorageKey,
    defaultSort,
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
}: UseMasterListStateArgs) {
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = useCallback(
        (mutate: (params: URLSearchParams) => void, opts?: { resetPage?: boolean, push?: boolean }) => {
            // react-router 6.3 has no functional-updater form, so derive the
            // next params from the current ones. Mutations are user-driven
            // (one at a time), so reading current `searchParams` is safe.
            const next = new URLSearchParams(searchParams);
            mutate(next);
            if (opts?.resetPage) next.delete('page');
            setSearchParams(next, { replace: !opts?.push });
        },
        [searchParams, setSearchParams],
    );

    // --- Search ---------------------------------------------------------
    const searchQuery = searchParams.get('q') ?? '';
    const setSearchQuery = useCallback(
        (value: string) => {
            updateParams((p) => {
                if (value) p.set('q', value);
                else p.delete('q');
            }, { resetPage: true });
        },
        [updateParams],
    );

    // --- Sort -----------------------------------------------------------
    const sortState = useMemo<SortState>(() => {
        const raw = searchParams.get('sort');
        if (raw === null) return defaultSort;
        if (raw === '') return { key: '', order: '' };
        const [key, order] = raw.split(':');
        if (key && (order === 'asc' || order === 'desc')) return { key, order };
        return defaultSort;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const toggleSort = useCallback(
        (key: string) => {
            // Cycle: unsorted/other → asc → desc → unsorted.
            let next: SortState;
            if (sortState.key !== key) next = { key, order: 'asc' };
            else if (sortState.order === 'asc') next = { key, order: 'desc' };
            else next = { key: '', order: '' };

            updateParams((p) => {
                if (next.key === defaultSort.key && next.order === defaultSort.order) {
                    p.delete('sort');
                } else if (next.key === '') {
                    p.set('sort', '');
                } else {
                    p.set('sort', `${next.key}:${next.order}`);
                }
            }, { resetPage: true });
        },
        // Primitive deps: callers pass `defaultSort` as a fresh object
        // literal each render, so depending on the object would defeat the
        // memoization.
        [updateParams, sortState, defaultSort.key, defaultSort.order],
    );

    // --- Pagination -----------------------------------------------------
    const page = useMemo(() => {
        const raw = Number(searchParams.get('page'));
        return Number.isInteger(raw) && raw >= 0 ? raw : 0;
    }, [searchParams]);

    const setPage = useCallback(
        (value: number) => {
            updateParams((p) => {
                if (value > 0) p.set('page', String(value));
                else p.delete('page');
            });
        },
        [updateParams],
    );

    const [rowsPerPage, setRowsPerPageState] = useState<number>(() => {
        const saved = localStorage.getItem(rowsPerPageStorageKey);
        const parsed = saved !== null ? Number(saved) : NaN;
        return rowsPerPageOptions.includes(parsed) ? parsed : 100;
    });

    const setRowsPerPage = useCallback(
        (value: number) => {
            localStorage.setItem(rowsPerPageStorageKey, String(value));
            setRowsPerPageState(value);
            setPage(0);
        },
        [rowsPerPageStorageKey, setPage],
    );

    // MUI `TablePagination` handler shapes — identical across the master
    // pages, so they live here rather than being re-declared per page.
    const handlePageChange = useCallback(
        (_: unknown, newPage: number) => setPage(newPage),
        [setPage],
    );

    const handleRowsPerPageChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => setRowsPerPage(parseInt(event.target.value, 10)),
        [setRowsPerPage],
    );

    // --- Tenant selection ----------------------------------------------
    const selectedTenantSlugs = useMemo<Set<string>>(() => {
        const raw = searchParams.get('tenants');
        if (raw === null) return new Set(accessibleTenants);
        if (raw === '') return new Set<string>();
        const wanted = new Set(raw.split(','));
        return new Set(accessibleTenants.filter((slug) => wanted.has(slug)));
    }, [searchParams, accessibleTenants]);

    const setSelectedTenantSlugs = useCallback(
        (nextSet: Set<string>) => {
            // `push` (not replace): each tenant-filter change is a history
            // step, so back navigation walks the filter states back to the
            // unfiltered starting view instead of leaving the all-tenant
            // view entirely.
            updateParams((p) => {
                const allSelected =
                    accessibleTenants.length > 0 &&
                    accessibleTenants.every((slug) => nextSet.has(slug));
                if (allSelected) p.delete('tenants');
                else p.set('tenants', accessibleTenants.filter((slug) => nextSet.has(slug)).join(','));
            }, { resetPage: true, push: true });
        },
        [updateParams, accessibleTenants],
    );

    const toggleTenantSelected = useCallback(
        (slug: string) => {
            const next = new Set(selectedTenantSlugs);
            if (next.has(slug)) next.delete(slug);
            else next.add(slug);
            setSelectedTenantSlugs(next);
        },
        [selectedTenantSlugs, setSelectedTenantSlugs],
    );

    const selectAllTenants = useCallback(
        () => setSelectedTenantSlugs(new Set(accessibleTenants)),
        [setSelectedTenantSlugs, accessibleTenants],
    );

    const clearAllTenants = useCallback(
        () => setSelectedTenantSlugs(new Set()),
        [setSelectedTenantSlugs],
    );

    const selectCustomerServiceTenants = useCallback(
        () => setSelectedTenantSlugs(new Set(getCustomerServiceTenants(accessibleTenants))),
        [setSelectedTenantSlugs, accessibleTenants],
    );

    // --- Sorting / pagination helpers ----------------------------------
    // Both are identical across the master pages, so they live here and
    // the pages only supply the page-specific comparator.
    const applySort = useCallback(
        <T>(rows: T[], compare: (a: T, b: T) => number): T[] => {
            if (!sortState.key || !sortState.order) return rows;
            const direction = sortState.order === 'asc' ? 1 : -1;
            return [...rows].sort((a, b) => compare(a, b) * direction);
        },
        [sortState],
    );

    const paginate = useCallback(
        <T>(rows: T[]): T[] => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [page, rowsPerPage],
    );

    // --- Drill into a tenant-scoped detail page ------------------------
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    /**
     * Switch the Redux tenant context to `tenantSlug` and navigate to
     * `detailUrl`. The current all-tenant URL (with its filters) is passed
     * as `fromMaster` so the topbar back button returns to the exact view
     * the user left.
     */
    const drillIntoTenant = useCallback(
        (tenantSlug: string, detailUrl: string) => {
            const fromMaster = `${location.pathname}${location.search}`;
            // Every tenant-scoped cache is cleared centrally on the tenant
            // change itself (`store/tenantCacheResetMiddleware`) — this used to
            // clear 4 of 28 slices by hand.
            dispatch(tenantSelected({ tenant: tenantSlug }));
            navigate(detailUrl, { state: { fromMaster } });
        },
        [dispatch, navigate, location.pathname, location.search],
    );

    return {
        accessibleTenants,
        drillIntoTenant,
        applySort,
        paginate,
        searchQuery,
        setSearchQuery,
        sortState,
        toggleSort,
        page,
        setPage,
        handlePageChange,
        rowsPerPage,
        rowsPerPageOptions,
        setRowsPerPage,
        handleRowsPerPageChange,
        selectedTenantSlugs,
        toggleTenantSelected,
        selectAllTenants,
        clearAllTenants,
        selectCustomerServiceTenants,
    };
}

export type MasterListState = ReturnType<typeof useMasterListState>;
